import { useState, useMemo, useEffect } from "react";
import { useBreadcrumbs } from "@app/components/Breadcrumbs";
import {
  Text,
  ActionButtonGroup,
  ActionButton,
  IllustratedMessage,
  Heading,
  Content,
  ProgressCircle,
  LabeledValue,
} from "@react-spectrum/s2";
import MagicWandIcon from "@react-spectrum/s2/icons/MagicWand";
import AudioWave from "@react-spectrum/s2/illustrations/linear/AudioWave";
import { TextToSpeech as AudioPlayer } from "@app/components/TextToSpeech";
import { VoicesList } from "@app/components/VoicesList";

import {
  useVoices,
  useGenerateSpeech,
  useFixPronunciation,
} from "@app/hooks/textToSpeech";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

const getGenderText = (gender) => {
  switch (gender.toLowerCase()) {
    case "f":
      return "Female";
    case "m":
      return "Male";
    case "nb":
      return "Non-binary";
    default:
      return "";
  }
};

export const TextToSpeech = () => {
  useBreadcrumbs([
    { label: "Admin", href: "/admin" },
    { label: "Text-to-speech" },
  ]);

  const [selectedVoiceId, setSelectedVoiceId] = useState(null);
  const [text, setText] = useState("");
  const [favorites, setFavorites] = useState(
    localStorage.getItem("BeStory__favoriteVoices")?.split(",") || [],
  );

  useEffect(() => {
    if (!favorites.includes("v123_1")) {
      setFavorites((prev) => [...prev, "v123_1"]);
      localStorage.setItem(
        "BeStory__favoriteVoices",
        [...favorites, "v123_1"].join(","),
      );
    }
  }, []);

  const { data: voices, isLoading, isError } = useVoices();
  const generateSpeech = useGenerateSpeech();
  const fixPronunciation = useFixPronunciation();

  const selectedVoice = useMemo(
    () => voices?.find((voice) => voice.voiceId === selectedVoiceId),
    [selectedVoiceId, voices],
  );

  return (
    <>
      <title>Text-to-speech | BeStory</title>
      <div
        className={style({
          display: "grid",
          gap: 24,
          width: "full",
          height: "calc(100vh - 6.5rem)",
          paddingX: 24,
          boxSizing: "border-box",
          gridTemplateAreas: ["heading heading", "voices content"],
          gridTemplateColumns: [320, "minmax(0, 1fr)"],
          gridTemplateRows: ["auto", "minmax(0, 1fr)"],
        })}
      >
        <div
          className={style({
            gridArea: "heading",
            font: "heading",
            marginBottom: 16,
          })}
        >
          Text-to-speech
        </div>
        <div
          className={style({
            gridArea: "voices",
            display: "flex",
            flexDirection: "column",
          })}
        >
          {/* Only a heading: a `LabeledValue` cannot contain the selectable list. */}
          <LabeledValue label="Voices" value="" />
          {isLoading && <div>Loading voices...</div>}
          {isError && <div>Failed to load voices.</div>}
          {voices && (
            <VoicesList
              voices={voices}
              voiceId={selectedVoiceId}
              onVoiceSelect={setSelectedVoiceId}
              styles={style({ height: "full" })}
            />
          )}
        </div>
        <div className={style({ gridArea: "content", zIndex: 0 })}>
          {!!selectedVoice && (
            <div
              className={style({
                display: "flex",
                flexDirection: "column",
                gap: 24,
                maxWidth: 1024,
                marginX: "auto",
              })}
            >
              <div
                className={style({
                  display: "flex",
                  gap: 8,
                  alignItems: "baseline",
                })}
              >
                <Text styles={style({ font: "heading-sm" })}>
                  {selectedVoice.displayName}
                </Text>
                <Text
                  styles={style({ font: "ui", color: "neutral-subdued" })}
                >{`${selectedVoice.ageGroup} • ${getGenderText(selectedVoice.gender)} • ${selectedVoice.ethnicity}`}</Text>
              </div>

              <AudioPlayer
                audioUrl={generateSpeech.data}
                voiceId={selectedVoice.voiceId}
                voices={voices}
                text={text}
                onTextChange={setText}
                onGenerate={generateSpeech.mutate}
                isPending={generateSpeech.isPending}
                isDisabled={fixPronunciation.isPending}
                onVoiceSelect={setSelectedVoiceId}
              >
                <ActionButtonGroup isQuiet>
                  <ActionButton
                    isDisabled={
                      !text.trim() ||
                      generateSpeech.isPending ||
                      fixPronunciation.isPending
                    }
                    onPress={() => fixPronunciation.mutate({ text })}
                  >
                    {fixPronunciation.isPending ? (
                      <ProgressCircle
                        aria-label="Generating pronunciations"
                        size="S"
                        staticColor="auto"
                        slot="icon"
                        isIndeterminate
                      />
                    ) : (
                      <MagicWandIcon />
                    )}
                    <Text>Fix pronunciation</Text>
                  </ActionButton>
                </ActionButtonGroup>
              </AudioPlayer>
            </div>
          )}
          {!selectedVoice && (
            <div
              className={style({
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                size: "full",
              })}
            >
              <IllustratedMessage orientation="horizontal">
                <AudioWave />
                <Heading>Select a voice</Heading>
                <Content>
                  Choose a voice from the list to preview its sample and view
                  details.
                </Content>
              </IllustratedMessage>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
